import React, { Fragment, useEffect, useState } from "react";
import { Container, Row, Input, Col, Card, CardBody, CardHeader } from "reactstrap";
import { Breadcrumbs } from "../../../../AbstractElements";
import axios from "axios";
import config from "../../../../config";
import WidgetWrapper from "./WidgetWrapper";
import TrafficTrend from "./TrafficTrend";
import TrafficByDay from "./TrafficByDay";
import ConversionByDay from "./ConversionByDay";
import { useTranslation } from 'react-i18next';

const TrafficDashboard = () => {

    const { t } = useTranslation();
    const concessionaire_id = localStorage.getItem("concessionaireId");
    const property_id = localStorage.getItem("propertyAccess");
    const [properties, setProperties] = useState([]);
    const [propertyName, setPropertyName] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedPropertyMallCode, setSelectedPropertyMallCode] = useState('');
    const [selectedPropertyId, setSelectedPropertyId] = useState('');
    const property_access = localStorage.getItem("propertyAccess");
    
    const [zones, setZones] = useState([]);
    const [floors, setFloors] = useState([]);
    const [entrances, setEntrances] = useState([]);
    const [devices, setDevices] = useState([]);
    const [selectedZone, setSelectedZone] = useState('');
    const [selectedFloor, setSelectedFloor] = useState('');
    const [selectedEntrance, setSelectedEntrance] = useState('');    
    const [selectedDevice, setSelectedDevice] = useState('');
    const [loading, setLoading] = useState(true); 

    const token = localStorage.getItem("sessiontoken");
    const configheader = {
        headers: {
            Authorization: `${token}`
        }
    };

    useEffect(() => {
        setLoading(true); 
        axios
            .post(
                `${config.hostname}/footfall/getfootfallpropertybyconcessionaire`,
                { property_concessionaire_id: concessionaire_id, property_access: property_access },
                configheader
            )
            .then((response) => {
                setProperties(response.data || []);
            })
            .catch((error) => {
                console.error("Error fetching properties:", error);
                setProperties([]); // ensure array
            })
            .finally(() => {
                setLoading(false); // 👈 stop loading after success/failure
            });
    }, []);

    const handlePropertyChange = async (e) => {
        const mallCode = e.target.value;
        if (mallCode === "") {
            setSelectedPropertyMallCode("");
            setSelectedPropertyId(""); 
            setZones([]);
            setFloors([]);
            setEntrances([]);
            setDevices([]);
            setSelectedZone("");
            setSelectedFloor("");
            setSelectedEntrance("");
            setSelectedDevice("");
            return; 
        }
        setSelectedPropertyMallCode(mallCode);
        let propertyId="";
        const selectedProperty = properties.find(property => property.property_mall_code === mallCode);
        if (selectedProperty) {
            propertyId = selectedProperty._id;
            setSelectedPropertyId(propertyId);
        }
        else{
            propertyId=property_id;
            setSelectedPropertyId(propertyId);
        }
        setSelectedFloor('');
        setSelectedZone('');
        setSelectedEntrance('');
        setSelectedDevice('');
        setFloors([]);
        setZones([]);
        setEntrances([]);
        setDevices([]);
        try {
            const response = await axios.post(
                `${config.hostname}/footfall/getzonesfloorsentrancesdevicesbyproperty`,
                { property_id: propertyId },
                configheader
            );

            const data = response.data;

            // Log for debugging
            console.log("Zones, Floors, Entrances, Devices response:", data);

            setZones(data.zones || []);
            setFloors(data.floors || []);
            setEntrances(data.entrances || []);
            setDevices(data.devices || []);
        } catch (err) {
            console.error("Failed to fetch zones/floors/entrances/devices:", err);
            setZones([]);
            setFloors([]);
            setEntrances([]);
            setDevices([]);
        }    
    };

    const handleFloorChange = async (e) => {
        const selectedFloor = e.target.value;
        setSelectedFloor(selectedFloor);
        setSelectedZone('');
        setSelectedEntrance('');
        setSelectedDevice('');
        setZones([]);
        setEntrances([]);
        setDevices([]);
        try {
            const response = await axios.post(
                `${config.hostname}/footfall/getzonesentrancesdevicesbypropertyfloor`,
                {
                    property_id: selectedPropertyId || "",
                    floor: selectedFloor || ""
                },
                configheader
            );

            const data = response.data;
            console.log("Zones and Entrances by Floor:", data);

            setZones(data.zones || []);
            setEntrances(data.entrances || []);
            setDevices(data.devices || []);
        } catch (err) {
            console.error("Failed to fetch zones/entrances/devices by floor:", err);
            setZones([]);
            setEntrances([]);
            setDevices([]);
        }
    };

    const handleZoneChange = async (e) => {
        const selected = e.target.value;
        setSelectedZone(selected);
        setSelectedEntrance('');
        setSelectedDevice('');
        setEntrances([]);
        setDevices([]);
        try {
            const response = await axios.post(
                `${config.hostname}/footfall/getEntrancesByPropertyFloorZone`,
                {
                    property_id: selectedPropertyId || "",
                    floor: selectedFloor || "",
                    zone: selected || ""
                },
                configheader
            );

            const data = response.data;
            console.log("Entrances by Property-Floor-Zone:", data);

            setEntrances(data.entrances || []);
            setDevices(data.devices || []);
        } catch (err) {
            console.error("Failed to fetch entrances/devices by property/floor/zone:", err);
            setEntrances([]);
            setDevices([]);
        }
    };

    const handleEntranceChange = async (e) => {
        const selected = e.target.value;
        setSelectedEntrance(selected);
        setSelectedDevice('');
        setDevices([]); // reset before fetch

        try {
            const response = await axios.post(
                `${config.hostname}/footfall/getDevicesByPropertyFloorZoneEntrances`,
                {
                    property_id: selectedPropertyId || "",
                    floor: selectedFloor || "",
                    zone: selectedZone || "",
                    entrance_name: selected || ""
                },
                configheader
            );

            const data = response.data;
            console.log("Devices by Property-Floor-Zone-Entrance:", data);

            setDevices(data.devices || []);
        } catch (err) {
            console.error("Failed to fetch devices by property/floor/zone/entrance:", err);
            setDevices([]);
        }
    };

    if (loading) {
        return (
            <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('Loading...')}</span>
                </div>
                <p className="mt-3">{t('Loading traffic data...')}</p>
            </div>
        );
    }

    if (!loading && properties.length === 0) {
        return (
            <Card className="text-center p-5">
                <h5>{t('No traffic data is available.')}</h5>
            </Card>
        );
    }
    return (
        <Fragment>
            <Card>
                <CardBody>
                    <Row className="mb-4"><Col md='4' >
                        <Input className="form-control form-control-info-fill" name="select" type="select"
                            onChange={handlePropertyChange}>
                            <option value="">{t('Select Property')}</option>
                            <option value="">{t('All')}</option>
                            {properties.map((property) => (
                                <option key={property.property_mall_code} value={property.property_mall_code}>
                                {property.property_name}
                                </option>
                            ))}
                        </Input>
                        </Col>
                        <Col md='4'>
                            <Input className="form-control form-control-success-fill" name="startDate" type="date"
                                onChange={(e) => setStartDate(e.target.value)}>
                            </Input>
                        </Col>
                        <Col md='4'>
                            <Input className="form-control form-control-info-fill" name="endDate" type="date"
                                onChange={(e) => setEndDate(e.target.value)}>
                            </Input>
                        </Col>

                    </Row>
                    <Row>
                        {floors.length > 0 && (
                            <Col md="4" className="mb-2">
                                <Input type="select" onChange={handleFloorChange} value={selectedFloor}>
                                    <option value="">{t('Select Floor')}</option>
                                    {floors.map((floor, idx) => (
                                        <option key={idx} value={floor}>{floor}</option>
                                    ))}
                                </Input>
                            </Col>
                        )}

                        {zones.length > 0 && (
                            <Col md="4" className="mb-2">
                                <Input type="select" onChange={handleZoneChange} value={selectedZone}>
                                    <option value="">{t('Select Zone')}</option>
                                    {zones.map((zone, idx) => (
                                        <option key={idx} value={zone}>{zone}</option>
                                    ))}
                                </Input>
                            </Col>
                        )}                        

                        {entrances.length > 0 && (
                            <Col md="4" className="mb-2">
                                <Input type="select" onChange={handleEntranceChange} value={selectedEntrance}>
                                    <option value="">{t('Select Entrance')}</option>
                                    {entrances.map((entrance, idx) => (
                                        <option key={idx} value={entrance}>{entrance}</option>
                                    ))}
                                </Input>
                            </Col>
                        )}

                        {devices.length > 0 && (
                            <Col md="4" className="mb-2">
                                <Input type="select" onChange={(e) => setSelectedDevice(e.target.value)} value={selectedDevice}>
                                    <option value="">{t('Select Device')}</option>
                                    {devices.map((device, idx) => (
                                        <option key={idx} value={device}>{device}</option>
                                    ))}
                                </Input>
                            </Col>
                        )}
                    </Row>
                </CardBody>
            </Card>
            <Container fluid={true}>
                <Row className="widget-grid">
                    <WidgetWrapper propertyId={selectedPropertyId} startDate={startDate} endDate={endDate} />
                    <TrafficTrend 
                        propertyId={selectedPropertyId}
                        floor={selectedFloor}
                        zone={selectedZone}
                        entrance={selectedEntrance}
                        device={selectedDevice}
                    />
                    <TrafficByDay 
                        propertyId={selectedPropertyId}
                        floor={selectedFloor}
                        zone={selectedZone}
                        entrance={selectedEntrance}
                        device={selectedDevice}
                    />
                    <ConversionByDay propertyId={selectedPropertyId}/>
                </Row>
            </Container>
        </Fragment>
    )
}

export default TrafficDashboard